/**
 * Wholesale Retailer Sample Request — Klaviyo Submission Handler
 * Captures UTM params, validates form, subscribes to Klaviyo list, fires event.
 */
(function () {
  'use strict';

  /* ── Config (read from section data attributes) ── */
  var formEl = document.getElementById('wsr-form');
  if (!formEl) return;

  var COMPANY_ID = formEl.dataset.klaviyoCompanyId || 'TH64eF';
  var LIST_ID = formEl.dataset.klaviyoListId || 'WjM6hR';
  var REVISION = '2024-10-15';
  var SUBSCRIBE_URL = 'https://a.klaviyo.com/client/subscriptions/?company_id=' + COMPANY_ID;
  var EVENT_URL = 'https://a.klaviyo.com/client/events/?company_id=' + COMPANY_ID;

  /* ── Spam guard: the hidden company_website honeypot, plus a minimum time between page load
     and submit. A bot that trips either one sees the normal confirmation, but nothing is sent
     to Klaviyo (no profile, no team alert, no confirmation email to a scraped address). ── */
  var FORM_READY_AT = Date.now();
  var MIN_FILL_MS = 4000;
  function looksLikeBot() {
    var trap = formEl.querySelector('input[name="company_website"]');
    var reason = (trap && trap.value) ? 'honeypot filled' : (Date.now() - FORM_READY_AT < MIN_FILL_MS ? 'submitted too fast' : '');
    if (reason && window.Shopify && Shopify.theme && Shopify.theme.role !== 'main') console.debug('Wholesale form spam guard:', reason);
    return !!reason;
  }

  /* ── Fail-safe submit helpers ──
     Privacy tools block a.klaviyo.com (AdGuard Tracking Protection, Firefox strict / private windows,
     Pi-hole's default list), and then the request used to be lost. Now: subscribe, then the event even if
     the subscribe failed, then a backup copy through Shopify's own /contact form on this domain (lands in
     info@). The error message only shows if all three fail. ── */
  var IS_DEV = !!(window.Shopify && Shopify.theme && Shopify.theme.role !== 'main');

  // E.164 only for a real North American number (area code and exchange can't start with 0 or 1, no N11
  // area codes). Anything else stays off the profile; the raw value still goes on the event.
  function toE164(raw) {
    var d = String(raw || '').split(/x|ext/i)[0].replace(/\D/g, ''); // drop an extension ("x123", "ext. 4")
    if (d.length === 11 && d.charAt(0) === '1') d = d.slice(1);
    if (!/^[2-9]\d{2}[2-9]\d{6}$/.test(d) || /^\d11/.test(d)) return '';
    return '+1' + d;
  }

  // DEV only (unpublished theme): ?test_backup=1 makes the Klaviyo calls fail so the backup path can be tested by hand.
  var FORCE_BACKUP = IS_DEV && /[?&]test_backup=1/.test(location.search);

  function postKlaviyo(url, payload) {
    if (FORCE_BACKUP) return Promise.reject(new Error('test_backup: Klaviyo forced off'));
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'revision': REVISION },
      body: JSON.stringify(payload)
    }).then(function (res) {
      if (res.ok) return res;
      return res.text().then(function (t) { throw new Error('HTTP ' + res.status + (t ? ' ' + t.slice(0, 160) : '')); });
    });
  }

  // Shopify's /contact needs an hCaptcha token, which Shopify's own script adds only when a real contact form is
  // submitted natively. So the backup fills the hidden {% form 'contact' %} in this section and submits it. The page
  // navigates; Shopify sends it back with ?contact_posted=true and the confirmation shows on load (below).
  var BACKUP_FORM_ID = 'wsr-backup-form';
  var BACKUP_FLAG = 'wholesaleBackupPending';
  function submitBackupForm(fields) {
    var f = document.getElementById(BACKUP_FORM_ID);
    if (!f) return false;
    Object.keys(fields).forEach(function (k) { if (f.elements[k]) f.elements[k].value = fields[k]; });
    // Shopify binds its captcha to a contact form on the first focus inside it, and cancels any submit before that.
    f.elements['contact[body]'].dispatchEvent(new FocusEvent('focusin', { bubbles: true }));
    var started = Date.now();
    (function submitWhenBound() {
      if (f.dataset.hcaptchaBound || f.dataset.recaptchaBound || Date.now() - started > 8000) {
        if (f.requestSubmit) {
          f.requestSubmit();
        } else {
          f.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
      } else {
        setTimeout(submitWhenBound, 150);
      }
    })();
    return true;
  }

  function sendBackup(subject, name, email, phone, lines) {
    var fields = { 'contact[Subject]': subject, 'contact[name]': name, 'contact[email]': email, 'contact[phone]': phone, 'contact[body]': lines.join('\n') };
    // Keep our own copy: if Shopify shows its "not a robot" check, the visitor comes back here and we resubmit.
    try { sessionStorage.setItem(BACKUP_FLAG, JSON.stringify({ name: name, fields: fields, tries: 1, at: Date.now() })); } catch (e) {}
    if (!submitBackupForm(fields)) return Promise.reject(new Error('backup form missing'));
    return new Promise(function (resolve, reject) {
      // The page navigates on success. Still here after 25s means the backup didn't go out either.
      setTimeout(function () {
        try { sessionStorage.removeItem(BACKUP_FLAG); } catch (e) {}
        reject(new Error('contact form did not submit (captcha)'));
      }, 25000);
    });
  }

  // Last resort: the visitor's own mail app, with the whole request already written.
  function mailtoLink(subject, lines) {
    var body = lines.join('\n');
    if (body.length > 1800) body = body.slice(0, 1800) + '\n...';
    return 'mailto:wholesale@magnoliacoffeeco.com?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(body);
  }

  function readBackupFlag() {
    try {
      var raw = sessionStorage.getItem(BACKUP_FLAG);
      if (raw) sessionStorage.removeItem(BACKUP_FLAG);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  /* ── UTM + Referrer capture on page load ── */
  document.addEventListener('DOMContentLoaded', function () {
    var params = new URLSearchParams(window.location.search);
    var utmMap = {
      'utm_source': 'wsr-utm-source',
      'utm_medium': 'wsr-utm-medium',
      'utm_campaign': 'wsr-utm-campaign',
      'utm_content': 'wsr-utm-content',
      'utm_term': 'wsr-utm-term'
    };

    Object.keys(utmMap).forEach(function (key) {
      var input = document.getElementById(utmMap[key]);
      if (input) {
        input.value = params.get(key) || '';
      }
    });

    var refInput = document.getElementById('wsr-referrer');
    if (refInput) {
      refInput.value = document.referrer || '';
    }
  });

  /* ── Account type (Retail / Bulk / Both): pre-filled from business type, same mapping as the account application ── */
  var BUSINESS_TYPE_TO_ACCOUNT_TYPE = {
    'Specialty Food / Grocery Store': 'Retail',
    'Gift / Lifestyle Boutique': 'Retail',
    'Café / Espresso Bar': 'Bulk',
    'Coffee Truck / Cart': 'Both', // trailers and carts brew, and both 10/2026 trailer leads asked for retail too
    'Restaurant': 'Bulk',
    'Hotel / B&B': 'Bulk',
    'Co-working Space': 'Bulk'
  };
  var accountTypeGroup = document.getElementById('wsr-account-type-group');
  var retailVolumeFields = document.getElementById('wsr-retail-volume-fields');
  var bulkVolumeFields = document.getElementById('wsr-bulk-volume-fields');
  var accountTypeManuallySet = false;

  function toggleVolumeBlock(block, show) {
    if (!block) return;
    block.classList.toggle('is-visible', show);
    block.querySelectorAll('[data-conditional-required]').forEach(function (f) {
      if (show) {
        f.setAttribute('required', '');
      } else {
        f.removeAttribute('required');
        f.classList.remove('is-invalid');
      }
    });
  }

  function updateVolumeVisibility(accountType) {
    toggleVolumeBlock(retailVolumeFields, accountType === 'Retail' || accountType === 'Both');
    toggleVolumeBlock(bulkVolumeFields, accountType === 'Bulk' || accountType === 'Both');
    if (accountType && accountTypeGroup) accountTypeGroup.classList.remove('is-invalid');
  }

  function checkedAccountType() {
    var el = formEl.querySelector('input[name="account_type"]:checked');
    return el ? el.value : '';
  }

  var businessTypeSelect = document.getElementById('wsr-business-type');
  if (businessTypeSelect) {
    businessTypeSelect.addEventListener('change', function () {
      if (accountTypeManuallySet) return;
      var defaultType = BUSINESS_TYPE_TO_ACCOUNT_TYPE[this.value];
      if (!defaultType) return;
      var radio = formEl.querySelector('input[name="account_type"][value="' + defaultType + '"]');
      if (radio) {
        radio.checked = true;
        updateVolumeVisibility(defaultType);
      }
    });
  }

  formEl.querySelectorAll('input[name="account_type"]').forEach(function (radio) {
    radio.addEventListener('change', function () {
      accountTypeManuallySet = true;
      updateVolumeVisibility(this.value);
    });
  });

  /* Clear a field's error state as soon as it's fixed, instead of waiting for the next submit */
  ['input', 'change'].forEach(function (type) {
    formEl.addEventListener(type, function (e) {
      var f = e.target;
      if (f && f.classList && f.classList.contains('is-invalid') && f.value && f.value.trim()) f.classList.remove('is-invalid');
    });
  });

  /* ── Validation ── */
  function validateForm() {
    var valid = true;
    var requiredFields = formEl.querySelectorAll('[required]');

    requiredFields.forEach(function (field) {
      field.classList.remove('is-invalid');
      if (!field.value.trim()) {
        field.classList.add('is-invalid');
        valid = false;
      }
    });

    // Account type (radio group)
    if (accountTypeGroup) {
      accountTypeGroup.classList.remove('is-invalid');
      if (!checkedAccountType()) {
        accountTypeGroup.classList.add('is-invalid');
        valid = false;
      }
    }

    // Email format
    var emailField = document.getElementById('wsr-email');
    if (emailField && emailField.value.trim()) {
      var emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(emailField.value.trim())) {
        emailField.classList.add('is-invalid');
        valid = false;
      }
    }

    // ZIP format
    var zipField = document.getElementById('wsr-zip');
    if (zipField && zipField.value.trim()) {
      var zipPattern = /^\d{5}(-\d{4})?$/;
      if (!zipPattern.test(zipField.value.trim())) {
        zipField.classList.add('is-invalid');
        valid = false;
      }
    }

    // Scroll to first invalid
    if (!valid) {
      var firstInvalid = formEl.querySelector('.is-invalid');
      if (firstInvalid) {
        firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstInvalid.focus();
      }
    }

    return valid;
  }

  /* ── Helpers ── */
  function val(id) {
    var el = document.getElementById(id);
    return el ? el.value.trim() : '';
  }

  function showError(msg) {
    var errEl = document.getElementById('wsr-form-error');
    if (errEl) {
      errEl.innerHTML = msg;
      errEl.removeAttribute('hidden');
      errEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  function hideError() {
    var errEl = document.getElementById('wsr-form-error');
    if (errEl) {
      errEl.setAttribute('hidden', '');
    }
  }

  /* ── Submit handler ── */
  formEl.addEventListener('submit', function (e) {
    e.preventDefault();
    hideError();

    if (!validateForm()) return;

    var submitBtn = document.getElementById('wsr-submit');
    var originalText = submitBtn.textContent;
    submitBtn.disabled = true;
    submitBtn.textContent = 'Submitting...';

    var firstName = val('wsr-first-name');
    var lastName = val('wsr-last-name');
    var email = val('wsr-email');
    var phone = val('wsr-phone');
    var businessName = val('wsr-business-name');
    var role = val('wsr-role');
    var businessType = val('wsr-business-type');
    var accountType = checkedAccountType();
    var retailVolume = (accountType === 'Retail' || accountType === 'Both') ? val('wsr-retail-volume') : '';
    var bulkVolume = (accountType === 'Bulk' || accountType === 'Both') ? val('wsr-bulk-volume') : '';
    // Readable combined string so reporting built on monthly_volume still reads, e.g. "Bulk: 10-20 bags (50-100 lbs)"
    var monthlyVolume = [retailVolume ? 'Retail: ' + retailVolume : '', bulkVolume ? 'Bulk: ' + bulkVolume : '']
      .filter(Boolean).join('; ');
    var currentBrands = val('wsr-current-brands');
    var address = val('wsr-street');
    var city = val('wsr-city');
    var state = val('wsr-state');
    var zip = val('wsr-zip');
    var referralSource = val('wsr-referral');
    var notes = val('wsr-notes');
    var utmSource = val('wsr-utm-source');
    var utmMedium = val('wsr-utm-medium');
    var utmCampaign = val('wsr-utm-campaign');
    var utmContent = val('wsr-utm-content');
    var utmTerm = val('wsr-utm-term');
    var referrerUrl = val('wsr-referrer');
    var requestedAt = new Date().toISOString();

    /* Step 1: Subscribe + update profile */
    var subscribePayload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Wholesale Retailer Sample Form',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                first_name: firstName,
                last_name: lastName,
                organization: businessName,
                properties: {
                  // Same property names as the account application (wholesale_lead_type carries the business type there)
                  wholesale_lead_type: businessType,
                  wholesale_role: role,
                  wholesale_business_type: businessType,
                  wholesale_account_type: accountType,
                  wholesale_retail_volume: retailVolume,
                  wholesale_bulk_volume: bulkVolume,
                  wholesale_monthly_volume: monthlyVolume,
                  wholesale_current_brands: currentBrands,
                  wholesale_referral_source: referralSource,
                  wholesale_notes: notes,
                  wholesale_status: 'sample_requested',
                  wholesale_sample_requested_at: requestedAt,
                  utm_source: utmSource,
                  utm_medium: utmMedium,
                  utm_campaign: utmCampaign,
                  utm_content: utmContent,
                  utm_term: utmTerm,
                  referrer_url: referrerUrl
                },
                location: {
                  address1: address,
                  city: city,
                  region: state,
                  zip: zip,
                  country: 'US'
                }
              }
            }
          }
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: LIST_ID
            }
          }
        }
      }
    };

    var profileAttrs = subscribePayload.data.attributes.profile.data.attributes;
    var phoneE164 = toE164(phone);
    if (phoneE164) profileAttrs.phone_number = phoneE164;

    var eventProperties = {
      business_name: businessName,
      first_name: firstName,
      last_name: lastName,
      role: role,
      email: email,
      phone: phone,
      business_type: businessType,
      account_type: accountType,
      retail_volume: retailVolume,
      bulk_volume: bulkVolume,
      monthly_volume: monthlyVolume,
      current_brands: currentBrands,
      address: address,
      city: city,
      state: state,
      zip: zip,
      referral_source: referralSource,
      notes: notes,
      utm_source: utmSource,
      utm_medium: utmMedium,
      utm_campaign: utmCampaign,
      utm_content: utmContent,
      utm_term: utmTerm,
      referrer_url: referrerUrl
    };
    var eventPayload = {
      data: {
        type: 'event',
        attributes: {
          metric: { data: { type: 'metric', attributes: { name: 'Wholesale Sample Requested' } } },
          profile: { data: { type: 'profile', attributes: { email: email } } },
          properties: eventProperties
        }
      }
    };

    function showConfirmation() {
      // Hide form, show confirmation
      formEl.setAttribute('hidden', '');
      var confirmation = document.getElementById('wsr-confirmation');
      if (confirmation) {
        confirmation.removeAttribute('hidden');
      }

      // Scroll to confirmation
      var formSection = document.getElementById('sample-form');
      if (formSection) {
        formSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    if (looksLikeBot()) return showConfirmation();

    var failures = [];
    function note(step, err) {
      var reason = step + ': ' + ((err && err.message) || String(err));
      failures.push(reason);
      if (IS_DEV) console.warn('Wholesale sample form fallback:', reason);
    }

    /* Step 1: subscribe + update the profile. A failure here never stops the request. */
    postKlaviyo(SUBSCRIBE_URL, subscribePayload)
      .catch(function (err) {
        note('subscribe', err);
        // Klaviyo refused it (4xx) with a phone on the profile: try once more without the phone.
        if (profileAttrs.phone_number && /^HTTP 4/.test(err && err.message)) {
          delete profileAttrs.phone_number;
          return postKlaviyo(SUBSCRIBE_URL, subscribePayload).catch(function (err2) { note('subscribe without phone', err2); });
        }
      })
      /* Step 2: the event (it creates the profile on its own if the subscribe failed) */
      .then(function () {
        if (failures.length) eventProperties.submit_fallback_reason = failures.join(' | ');
        return postKlaviyo(EVENT_URL, eventPayload).then(function () { return 'klaviyo'; });
      })
      /* Step 3: Klaviyo unreachable, so send a backup copy through Shopify's contact form (info@) */
      .catch(function (err) {
        note('event', err);
        var lines = ['WHOLESALE SAMPLE REQUEST (backup copy: Klaviyo could not be reached from this visitor\'s browser)', ''];
        Object.keys(eventProperties).forEach(function (k) {
          if (k !== 'submit_fallback_reason' && eventProperties[k]) lines.push(k + ': ' + eventProperties[k]);
        });
        lines.push('', 'submit_fallback_reason: ' + failures.join(' | '), 'page: ' + location.href);
        return sendBackup('Wholesale sample request (backup)', (firstName + ' ' + lastName).trim(), email, phone, lines)
          .then(function () { return 'backup'; });
      })
      .then(function (path) {
        // GA4 dataLayer push
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'wholesale_sample_request_submitted',
          submit_path: path,
          business_type: businessType,
          account_type: accountType,
          retail_volume: retailVolume,
          bulk_volume: bulkVolume,
          monthly_volume: monthlyVolume
        });

        showConfirmation();
      })
      .catch(function (err) {
        note('backup', err);
        console.error('Wholesale form submission failed:', failures.join(' | '));
        var mailLines = Object.keys(eventProperties).filter(function (k) { return k !== 'submit_fallback_reason' && eventProperties[k]; })
          .map(function (k) { return k + ': ' + eventProperties[k]; });
        showError(
          'Sorry, your request didn\'t go through. <a href="' + mailtoLink('Wholesale sample request', mailLines) + '">Email us your request</a> (it opens your email with everything filled in), or write to <a href="mailto:wholesale@magnoliacoffeeco.com">wholesale@magnoliacoffeeco.com</a> and we\'ll get your samples started.'
        );
        submitBtn.disabled = false;
        submitBtn.textContent = originalText;
      });
  });

  /* ── Back from a backup post: Shopify returns to this page with ?contact_posted=true ── */
  (function () {
    var pending = readBackupFlag();
    var posted = /[?&]contact_posted=true/.test(location.search);
    var backFromCheck = /[?&]form_type=contact/.test(location.search);
    if (!posted && backFromCheck && pending && pending.fields && pending.tries < 3) {
      // Back from Shopify's "not a robot" check: send the saved backup again.
      pending.tries += 1;
      try { sessionStorage.setItem(BACKUP_FLAG, JSON.stringify(pending)); } catch (e) {}
      submitBackupForm(pending.fields);
      return;
    }
    if (posted) {
      formEl.setAttribute('hidden', '');
      var confirmation = document.getElementById('wsr-confirmation');
      if (confirmation) confirmation.removeAttribute('hidden');
      var formSection = document.getElementById('sample-form');
      if (formSection) formSection.scrollIntoView({ block: 'start' });
      if (pending) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'wholesale_sample_request_submitted', submit_path: 'backup' });
      }
    } else if (pending) {
      // Shopify didn't accept the backup either
      var saved = pending.fields ? pending.fields['contact[body]'].split('\n') : [];
      showError(
        'Sorry, your request didn\'t go through. <a href="' + mailtoLink('Wholesale sample request', saved) + '">Email us your request</a> (it opens your email with everything filled in), or write to <a href="mailto:wholesale@magnoliacoffeeco.com">wholesale@magnoliacoffeeco.com</a> and we\'ll get your samples started.'
      );
    }
  })();

  /* ── Smooth scroll for CTA ── */
  document.addEventListener('click', function (e) {
    var link = e.target.closest('a[href="#sample-form"]');
    if (link) {
      e.preventDefault();
      var target = document.getElementById('sample-form');
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  });
})();
